import { Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  SystemFilter,
  UserFilter,
} from '../../../models/api/reports/all-filters-response';
import { PersonalizedReport } from '../../../models/api/reports/all-reports-response';
import {
  FilterDetail,
  FilterDetailResponse,
} from '../../../models/api/reports/filter-detail-response';
import { MyReportDetails } from '../../../models/api/reports/my-report-details';
import { Action } from '../../../models/core/action';
import { ReportsService } from '../../../services/reports/reports.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { ValidatorsEx } from '../../../validators/validators-ex';
import { BaseComponent } from '../../base/base.component';
import { BackButtonComponent } from '../../core/buttons/back-button.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { IconButtonComponent } from '../../core/buttons/icon-button.component';
import { RenameDialogComponent } from '../../core/dialogs/rename-dialog.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { CardComponent } from '../../core/panels/card.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { HomeCardComponent } from '../../home/home-card.component';
import { FilterViewComponent } from '../core/filter-view.component';
import { ReportFieldViewComponent } from '../core/report-field-view.component';
import { ShareDialogComponent } from '../core/share-dialog.component';
import { SortFieldViewComponent } from '../core/sort-field-view.component';
import { MyRequestViewComponent } from './my-request-view.component';

@Component({
  selector: 'app-my-report-page',
  imports: [
    RouterLink,
    BackButtonComponent,
    FilterViewComponent,
    CardComponent,
    ActionRowComponent,
    ButtonComponent,
    PageHeaderComponent,
    IconButtonComponent,
    MenuComponent,
    MyRequestViewComponent,
    ReportFieldViewComponent,
    SortFieldViewComponent,
    LoadingPanelComponent,
    ShareDialogComponent,
    RenameDialogComponent,
    HomeCardComponent,
  ],
  template: `
    <app-page-header
      [label]="myReportsData?.reportName"
      labelHeader="Personalized report"
      labelCase="none"
      headerSize="h1"
    >
      <ng-template #right>
        <app-action-row>
          <app-button
            right1
            label="Edit"
            icon="fa-light fa-edit"
            title="Edit report"
            (click)="editReport()"
          />
          <app-icon-button
            right2
            icon="fa-light fa-ellipsis-vertical"
            emphasis="Low"
            title="Actions"
            [border]="false"
            (click)="rowMenu.toggle($event)"
          /> </app-action-row
      ></ng-template>
    </app-page-header>

    <div class="tw-flex">
      <app-back-button
        left1
        routerLink="/reports"
        [queryParams]="{ tab: 'reports' }"
      >
        Back
      </app-back-button>
    </div>
    <div class="tw-grow tw-justify-center tw-w-full">
      <div class="tw-min-w-0 tw-w-[100%] tw-pt-4 sm:tw-pt-8 tw-mb-4">
        <app-home-card type="Large">
          <div class="tw-min-w-0 tw-w-[100%] tw-p-4 sm:tw-p-8">
            <app-loading-panel
              [state]="loading() ? 'Loading' : 'Loaded'"
              loadingStyle="Box"
              [loadingHeight]="500"
              loadingClasses="tw-pb-8"
            >
              <ng-template #loaded>
                <div class="tw-pb-8">
                  <app-my-request-view
                    [myReportsData]="myReportsData"
                    [myReportDetails]="myReportsDetails.outputDetails"
                  />
                </div>
                <div
                  class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 sm:tw-gap-8 tw-w-full tw-pb-4 tw-min-w-0"
                >
                  @if (filterDetails) {
                    <app-card
                      label="Filter"
                      class="tw-basis-1/2 tw-min-w-0 tw-overflow-hidden"
                    >
                      <app-filter-view
                        [dataSource]="filterDetails.filterDetail"
                      />
                    </app-card>
                  }
                  <div
                    class="tw-flex tw-gap-4 sm:tw-gap-8 tw-flex-col tw-min-w-0"
                    [class.tw-basis-1/2]="filterDetails"
                    [class.tw-w-full]="!filterDetails"
                  >
                    <app-card
                      label="Report fields"
                      labelCase="titlecase"
                      class="tw-w-full tw-min-w-0"
                    >
                      <app-report-field-view
                        [reportFields]="myReportsDetails.reportFields"
                      />
                    </app-card>
                    <app-card
                      label="Sort fields"
                      labelCase="titlecase"
                      class="tw-w-full tw-min-w-0"
                    >
                      <app-sort-field-view
                        [sortBreakFields]="myReportsDetails.sortFields"
                      />
                    </app-card>
                  </div>
                </div>
              </ng-template>
            </app-loading-panel>
          </div>
        </app-home-card>
      </div>
    </div>

    <app-menu [items]="rowActions" #rowMenu (click)="menuClick($event)" />
    <app-share-dialog #shareDialog />
    <app-rename-dialog
      label="Rename report"
      #renameDialog
      [extraValidators]="reportNameValidators"
      (rename)="renameReport($event)"
    />
    <app-rename-dialog
      label="Copy report"
      buttonLabel="Copy"
      [allowSameValue]="true"
      #copyDialog
      [extraValidators]="reportNameValidators"
      (rename)="doCopyReport($event)"
    />
  `,
  styles: ``,
})
export class MyReportPageComponent extends BaseComponent implements OnInit {
  @ViewChild('shareDialog')
  shareDialog: ShareDialogComponent;

  @ViewChild('renameDialog')
  renameDialog: RenameDialogComponent;

  @ViewChild('copyDialog')
  copyDialog: RenameDialogComponent;

  rowActions: Action[] = [
    { label: 'New schedule', key: 'schedule' },
    { label: 'Copy', key: 'copy' },
    { label: 'Rename', key: 'rename' },
    { label: 'Share', key: 'share' },
    { label: 'Delete', key: 'delete' },
  ];

  reportNameValidators = [
    ValidatorsEx.alphaNumericWith(
      '/-_ ',
      'Slashes (/), dashes (-), and underscores (_) are the only special characters allowed in report name.',
    ),
  ];

  filterDetail = signal<FilterDetail | null>(null);
  myReportsData: PersonalizedReport;
  myReportsDetails: MyReportDetails = {
    outputDetails: [],
    reportFields: [],
    sortFields: [],
  };

  filterDetails: FilterDetailResponse | null = null;

  filters: (SystemFilter | UserFilter)[] = [];

  loading = signal(false);

  private readonly reportsService = inject(ReportsService);
  private readonly snackbar = inject(Snackbar);

  async ngOnInit() {
    this.loading.set(true);

    // Try to get report from service (normal navigation or sessionStorage restore)
    const report =
      this.reportsService.currentDetailReport() as PersonalizedReport;

    // If not available, redirect back to reports list
    if (!report) {
      this.router.navigate(['/reports'], { replaceUrl: true });
      return;
    }

    this.myReportsData = report;

    // Handle redirects for archived completed/scheduled reports
    if (this.myReportsData.type === 'completed') {
      this.reportsService.navigateToDetail(this.myReportsData, 'completed');
      return;
    } else if (this.myReportsData.type === 'scheduled') {
      this.reportsService.navigateToDetail(this.myReportsData, 'scheduled');
      return;
    }

    const filterId = this.myReportsData?.filterId;
    const hasFilter = !!filterId;
    const [myReportDetails, allFilters] = await Promise.all([
      this.api.reports.myReportDetails(this.myReportsData.requestId, 'JURIS'),
      this.api.filters.getAllFilters(
        'JURIS',
        this.myReportsData.model,
        this.myReportsData?.contract,
      ),
    ]);

    this.myReportsDetails = myReportDetails;

    // Only fetch filter details if report has a valid filter
    if (hasFilter) {
      this.filterDetails = await this.api.filters.getFilterDetail(
        filterId,
        this.myReportsData?.contract,
      );
      this.filters = [...allFilters.standardFilters, ...allFilters.userFilters];
      const matchedFilter = this.filters.find(
        (f) => f.filterId === this.filterDetails.filterDetail.filterId,
      );
      if (matchedFilter) {
        this.filterDetails.filterDetail.filterName = matchedFilter.filterName;
      }
    }

    this.loading.set(false);
  }

  menuClick(action: Action) {
    if (action.key === 'delete') {
      this.confirm.show({
        label: 'Delete report',
        message: `Are you sure you want to delete "${this.myReportsData.reportName}"?`,
        width: '35rem',
        data: this.myReportsData,
        confirm: (data: PersonalizedReport) => this.confirmed(data),
      });
    } else if (action.key === 'schedule') {
      this.scheduleReport();
    } else if (action.key === 'copy') {
      this.copyDialog.show(this.myReportsData.reportName + ' - Copy');
    } else if (action.key === 'rename') {
      this.renameDialog.show(this.myReportsData.reportName);
    } else if (action.key === 'share') {
      this.shareDialog.show(
        'report',
        this.myReportsData.requestId,
        parseInt(this.myReportsData.contract, 10),
        this.myReportsData.reportName,
        this.myReportsData.reportMasterId,
      );
    }
  }

  async confirmed(report: PersonalizedReport) {
    await this.reportsService.deletePersonalizedReport(report.requestId);
    this.router.navigate(['/reports'], { replaceUrl: true });
  }

  async editReport() {
    await this.reportsService.loadPersonalizedRequestForEditing(
      this.myReportsData.requestId,
      'JURIS',
      this.myReportsData.model,
      this.myReportsData.contract,
    );
    this.reportsService.setMode('edit-personalized');
    this.router.navigate(['/reports/edit-report'], {
      queryParams: { step: 'configure' },
    });
  }

  async scheduleReport() {
    await this.reportsService.loadPersonalizedRequestForEditing(
      this.myReportsData.requestId,
      'JURIS',
      this.myReportsData.model,
      this.myReportsData.contract,
    );
    this.reportsService.setMode('schedule-personalized');
    this.router.navigate(['/reports/edit-report'], {
      queryParams: { step: 'delivery' },
    });
  }

  async doCopyReport(newName: string) {
    this.copyDialog.setLoading(true);
    await this.reportsService.copyPersonalizedReport(
      this.myReportsData.requestId,
      newName,
    );
    this.snackbar.show('Report copied', 'Success', 'fa-light fa-copy', 3000);
    this.copyDialog.close();
    this.router.navigate(['/reports'], { replaceUrl: true });
  }

  async renameReport(newName: string) {
    this.renameDialog.setLoading(true);

    await this.reportsService.renamePersonalizedReport(
      this.myReportsData.requestId,
      newName,
    );

    this.myReportsData.reportName = newName;
    this.snackbar.show(
      'Report renamed successfully',
      'Success',
      'fa-light fa-check',
      3000,
    );

    this.renameDialog.close();
  }
}
